import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthState, LoginRequest } from '@/api/login/type';
import { api } from '@/api/login/api';
import {
    toastError,
    toastSuccess,
} from '@components/common/Toast/CustomToast.tsx';

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            isAuthenticated: false,
            accessToken: null,
            expireTime: null,
            isLoading: false,

            login: async (credentials: LoginRequest) => {
                set({ isLoading: true });

                try {
                    const response = await api.login(credentials);

                    if (response.code === 0) {
                        const { accessToken, expireTime } = response.data;

                        set({
                            isAuthenticated: true,
                            accessToken,
                            expireTime,
                            isLoading: false,
                        });

                        toastSuccess('로그인에 성공했습니다.');
                        return { success: true };
                    } else {
                        set({ isLoading: false });
                        const errorMessage =
                            response.message || '로그인에 실패했습니다.';
                        toastError(errorMessage);
                        return { success: false, error: errorMessage };
                    }
                } catch (error) {
                    set({ isLoading: false });
                    const errorMessage =
                        error instanceof Error
                            ? error.message
                            : '로그인에 실패했습니다.';
                    return { success: false, error: errorMessage };
                }
            },

            logout: async () => {
                try {
                    await api.logout();
                } catch (error) {
                    console.error('Logout API failed:', error);
                } finally {
                    set({
                        isAuthenticated: false,
                        accessToken: null,
                        expireTime: null,
                    });
                    toastSuccess('로그아웃 되었습니다.');
                }
            },

            setLoading: (loading: boolean) => {
                set({ isLoading: loading });
            },

            // 개선된 토큰 만료 확인
            checkTokenExpiry: () => {
                const { expireTime, isAuthenticated } = get();

                // 인증되지 않은 상태면 false 반환
                if (!isAuthenticated) {
                    return false;
                }

                // expireTime이 없으면 유효하지 않음
                if (!expireTime) {
                    get().logout();
                    toastError('토큰 정보가 없습니다. 다시 로그인해주세요.');
                    return false;
                }

                // 토큰 만료 확인 (5분 여유를 두고 검사)
                const bufferTime = 5 * 60 * 1000; // 5분
                const currentTime = Date.now();

                if (currentTime + bufferTime > expireTime) {
                    get().logout();
                    toastError('토큰이 만료되었습니다. 다시 로그인해주세요.');
                    return false;
                }

                return true;
            },

            // 토큰이 곧 만료될 때 알림 (선택사항)
            checkTokenWarning: () => {
                const { expireTime, isAuthenticated } = get();

                if (!isAuthenticated || !expireTime) return;

                const warningTime = 10 * 60 * 1000; // 10분 전 경고
                const currentTime = Date.now();

                if (
                    currentTime + warningTime > expireTime &&
                    currentTime < expireTime
                ) {
                    toastError('토큰이 곧 만료됩니다. 작업을 저장해주세요.');
                }
            },
        }),
        {
            name: 'auth-storage',
            partialize: state => ({
                isAuthenticated: state.isAuthenticated,
                accessToken: state.accessToken,
                expireTime: state.expireTime,
            }),
        },
    ),
);
